import { useEffect, useState } from "react";
import { Button } from "src/components/buttons/shadcn-button";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Employee } from "src/redux/types/_employee.type";
import Search from "src/components/input/Search";
import { Trash } from "lucide-react";
import EmployeeListTable from "./EmployeeListTable";
import { useEmployees } from "./hook/useFetchEmployees";
import { Filters } from "./hook/useFetchArchives";
import { useDebounce } from "use-debounce";
import { CustomerListPagination } from "../../ManageCustomers/components/customer-pagination";
import EmployeeRegisterForm from "../Register/employee-register-form";

interface EmployeeListProps {
  edit: (emp: Employee) => void;
}

function updateSearchParams(
  key: string,
  value: string,
  setSearchParams: (params: URLSearchParams) => void,
  searchParams: URLSearchParams,
) {
  const params = new URLSearchParams(searchParams);

  if (value) {
    params.set(key, value.trim());
    if (key === "search") {
      params.set("page", "1");
    }
  } else {
    params.delete(key);
    if (key === "search") {
      params.set("page", "1");
    }
  }

  setSearchParams(params);
}

const EmployeeList = ({ edit }: EmployeeListProps) => {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const page = parseInt(searchParams.get("page") || "1", 10);
  const pagesize = 10;

  const name = searchParams.get("fullname") || "";
  const employee_code = searchParams.get("employee_code") || "";

  const [debouncedName] = useDebounce(name, 500);
  const [debouncedCode] = useDebounce(employee_code, 500);

  const [failSafePageNumber, setFailSafePageNumber] = useState<number>(1);

  const filters: Filters = {
    page,
    pagesize,
    name: debouncedName,
    employee_code: debouncedCode,
  };

  const { data: employees = [], isLoading, error, isFetching } = useEmployees(filters);

  const [showForm, setShowForm] = useState(false);

  const hasNextPage = employees?.length === pagesize;
  const hasPrevPage = page > 1;

  useEffect(() => {
    if (!employees) return;
    if (employees?.length > 0) {
      setFailSafePageNumber(page);
    } else if (failSafePageNumber !== page) {
      toast.info("No Page!");
      setSearchParams({
        ...Object.fromEntries(searchParams),
        page: failSafePageNumber.toString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees]);

  const handleNextPage = () => {
    setFailSafePageNumber(page);
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (page + 1).toString(),
    });
  };

  const handlePrevPage = () => {
    if (page === 1) {
      toast.info("This is the first page");
      return;
    }
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (page - 1).toString(),
    });
  };

  const handleJumpToPage = (page: number) => {
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: page.toString(),
    });
  };

  const handleClear = () => {
    setSearchParams();
  };

  return (
    <div className="w-full pt-6">
      <div className="flex justify-between mb-4 sticky top-0 backdrop-blur-lg">
        <h2 className="text-xl font-semibold mb-4 text-left">
          Employee Database
        </h2>

        <div className="flex gap-4">
          <Search
            value={name}
            placeholder="Search by Name"
            onChange={(value) =>
              updateSearchParams(
                "fullname",
                value,
                setSearchParams,
                searchParams,
              )
            }
          />
          <Search
            value={employee_code}
            placeholder="Search by Employee Code"
            onChange={(value) =>
              updateSearchParams(
                "employee_code",
                value,
                setSearchParams,
                searchParams,
              )
            }
          />
          <button
            type="button"
            onClick={handleClear}
            className="px-4 py-2 h-10 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-red-50 hover:text-red-600 transition"
          >
            <Trash />
          </button>
        </div>

        <div className="flex gap-6">
          <Button
            className="bg-accent text-white rounded-lg hover:bg-[#083A73] transition"
            onClick={() => navigate("/hr/employees/archived")}
          >
            Archive
          </Button>

          <Button
            onClick={() => setShowForm((prev) => !prev)}
            className="bg-accent text-white rounded-lg hover:bg-[#083A73] transition"
          >
            + Add Employee
          </Button>
        </div>
      </div>

      {/* Employee Form (Full Screen when open) */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-card overflow-y-auto p-6">
          {/* Close button */}
          <div className="flex justify-end mb-4 fixed top-4 right-6">
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 bg-red-600 text-foreground rounded-lg hover:bg-red-700 transition"
            >
              ✕ Close
            </button>
          </div>

          {/* Form */}
          <div>
            <div className="my-6">
              <h2 className="text-2xl text-center font-semibold">
                Employee Registration Form
              </h2>
            </div>
            <EmployeeRegisterForm />
          </div>
        </div>
      )}

      {/* Employee Table */}
      <div className="bg-card rounded-xl shadow-lg p-6">
        {isLoading && (
          <p className="text-gray-500">
            Loading employees...{" "}
            <span className="loading loading-dots loading-sm"></span>
          </p>
        )}
        {error && <p className="text-red-500">{error.message}</p>}
        {!isLoading && !error && employees.length === 0 && (
          <p className="text-gray-500">No employees found.</p>
        )}
        <EmployeeListTable employees={employees} edit={edit} />
      </div>
      <div className="flex-grow my-3">
        <CustomerListPagination
          currentPageNumber={page}
          onNext={handleNextPage}
          onPrev={handlePrevPage}
          onJumpToPage={handleJumpToPage}
          isFetching={isFetching}
          hasPrev={hasPrevPage}
          hasNext={hasNextPage}
        />
      </div>
    </div>
  );
};

export default EmployeeList;
